import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiPlus, PiTrash } from "react-icons/pi";

export default function FeaturesCards({ content, onContentChange, isSelected }: SectionContentProps) {
    const fontFamily = content.fontFamily || "'Inter', sans-serif";
    const primaryColor = content.colorPalette?.primary || "#3B82F6";
    const secondaryColor = content.colorPalette?.secondary || "#1E40AF";

    // Default features if none exist
    const features = content.features || [
        { title: "Feature 1", description: "This is a detailed description of the first feature card." },
        { title: "Feature 2", description: "This is a detailed description of the second feature card." },
        { title: "Feature 3", description: "This is a detailed description of the third feature card." },
    ];

    const addFeature = () => {
        const newFeatures = [...features, { title: "New Feature", description: "New feature description goes here." }];
        onContentChange?.({ ...content, features: newFeatures });
    };

    const removeFeature = (idx: number) => {
        const newFeatures = features.filter((_: any, i: number) => i !== idx);
        onContentChange?.({ ...content, features: newFeatures });
    };

    const updateFeature = (idx: number, field: string, value: string) => {
        const newFeatures = [...features];
        newFeatures[idx] = { ...newFeatures[idx], [field]: value };
        onContentChange?.({ ...content, features: newFeatures });
    };

    return (
        <div className="mx-auto max-w-7xl" style={{ fontFamily }}>
            {/* Section Title */}
            <div className="mb-16 text-center max-w-3xl mx-auto">
                <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight sm:text-5xl mb-4">
                    <EditableText
                        value={content.title || "Our Key Features"}
                        onChange={(val) => onContentChange?.({ ...content, title: val })}
                        as="span"
                        placeholder="Section Title"
                    />
                </h2>
                <div className="h-1.5 w-24 bg-gradient-to-r from-blue-600 to-indigo-600 mx-auto rounded-full"
                    style={{ background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` }} />
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {features.map((feature: any, idx: number) => (
                    <div
                        key={idx}
                        className="group relative flex flex-col p-8 bg-white rounded-3xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                    >
                        {isSelected && (
                            <button
                                onClick={(e) => { e.stopPropagation(); removeFeature(idx); }}
                                className="absolute top-4 right-4 hidden group-hover:flex h-8 w-8 items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 border border-red-100 transition-colors z-10"
                                title="Remove Feature"
                            >
                                <PiTrash className="h-4 w-4" />
                            </button>
                        )}

                        {/* Number Badge */}
                        <div
                            className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold text-white mb-6 shadow-md transform group-hover:scale-110 transition-transform duration-300"
                            style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                        >
                            {idx + 1}
                        </div>

                        {/* Content */}
                        <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors"
                                style={{ color: getContentColor(primaryColor, 'text') }}>
                                <EditableText
                                    value={feature.title}
                                    onChange={(val) => updateFeature(idx, "title", val)}
                                    as="span"
                                    placeholder="Feature Title"
                                />
                            </h3>
                            <p className="text-gray-600 leading-relaxed">
                                <EditableText
                                    value={feature.description}
                                    onChange={(val) => updateFeature(idx, "description", val)}
                                    as="span"
                                    placeholder="Feature Description"
                                />
                            </p>
                        </div>

                        {/* Decorative bottom line */}
                        <div
                            className="w-0 group-hover:w-full h-1 mt-6 rounded-full transition-all duration-500"
                            style={{ background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` }}
                        />
                    </div>
                ))}

                {isSelected && (
                    <button
                        onClick={addFeature}
                        className="flex min-h-[300px] flex-col items-center justify-center rounded-3xl border-2 border-dashed border-gray-300 bg-gray-50/50 text-gray-400 hover:border-blue-400 hover:bg-blue-50/50 hover:text-blue-500 transition-all group"
                    >
                        <div className="w-16 h-16 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center mb-4 group-hover:border-blue-200 group-hover:scale-110 transition-all shadow-sm">
                            <PiPlus className="h-8 w-8" />
                        </div>
                        <span className="text-lg font-medium">Add New Card</span>
                    </button>
                )}
            </div>
        </div>
    );
}

// Helper to handle dynamic colors if needed, though mostly using inline styles for the main colors
function getContentColor(color: string, type: 'text' | 'bg') {
    return undefined; // Let CSS classes handle default, override via style if needed
}
